// ABOUTME: Dialog for generating bingo square suggestions from a topic via AI.
// ABOUTME: Manages idle, loading, review, and error states; delegates API calls to the parent page.
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import CloseIcon from '@mui/icons-material/Close'
import { useAiSquareGeneration } from '../hooks/useAiSquareGeneration'

interface Props {
  open: boolean
  onClose: () => void
  onGenerate: (topic: string) => Promise<string[]>
  onAddSquares: (titles: string[]) => Promise<void>
  defaultTopic?: string
}

export function AiGenerateDialog({ open, onClose, onGenerate, onAddSquares, defaultTopic = '' }: Props) {
  const { topic, setTopic, state, suggestions, addError, generate, addToPack, removeSuggestion, retry, reset } =
    useAiSquareGeneration({ onGenerate, onAddSquares, defaultTopic })

  function handleClose() {
    reset()
    onClose()
  }

  async function handleAddToPackClick() {
    const success = await addToPack()
    if (success) handleClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Generate Squares with AI</DialogTitle>
      <DialogContent>
        {(state === 'idle' || state === 'generating') && (
          <Box pt={1}>
            <TextField
              label="Topic"
              fullWidth
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              disabled={state === 'generating'}
              placeholder='e.g. "office bingo" or "90s TV shows"'
            />
            {state === 'generating' && (
              <Box display="flex" justifyContent="center" pt={3}>
                <CircularProgress />
              </Box>
            )}
          </Box>
        )}

        {state === 'reviewing' && (
          <Stack spacing={1}>
            {addError && <Alert severity="error">{addError}</Alert>}
            <List dense>
              {suggestions.map((s, i) => (
                <ListItem
                  key={i}
                  secondaryAction={
                    <IconButton aria-label="remove" edge="end" onClick={() => removeSuggestion(i)}>
                      <CloseIcon />
                    </IconButton>
                  }
                >
                  <ListItemText primary={s} />
                </ListItem>
              ))}
            </List>
          </Stack>
        )}

        {state === 'adding' && (
          <Box display="flex" justifyContent="center" pt={3}>
            <CircularProgress />
          </Box>
        )}

        {state === 'error' && (
          <Alert severity="error">
            Something went wrong generating suggestions. Please try again.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        {(state === 'idle' || state === 'generating') && (
          <Button
            onClick={() => generate()}
            disabled={!topic.trim() || state === 'generating'}
            variant="contained"
          >
            Generate
          </Button>
        )}
        {state === 'reviewing' && (
          <Button onClick={handleAddToPackClick} variant="contained">
            Add to Pack
          </Button>
        )}
        {state === 'error' && (
          <Button onClick={retry} variant="contained">
            Try Again
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
